import React from 'react';

interface RiskMitigationItem {
  id: string;
  customerName: string;
  comment: string;
  value: number;
}

interface RiskMitigationBoxProps {
  title: string;
  items: RiskMitigationItem[];
  type: 'risk' | 'mitigation';
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `£${Math.round(value / 1000)}K`;
  return `£${value.toLocaleString()}`;
};

export const RiskMitigationBox: React.FC<RiskMitigationBoxProps> = ({
  title,
  items,
  type,
}) => {
  const isRisk = type === 'risk';
  const borderColor = isRisk ? 'border-red-500' : 'border-green-500';
  const headerBg = isRisk ? 'bg-red-500/20' : 'bg-green-500/20';
  const headerText = isRisk ? 'text-red-400' : 'text-green-400';
  const valueColor = isRisk ? 'text-red-400' : 'text-green-400';
  const totalValue = items.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className={`bg-surface border-2 ${borderColor} rounded-lg overflow-hidden`}>
      {/* Header */}
      <div className={`${headerBg} px-6 py-4 flex justify-between items-center`}>
        <h3 className={`text-xl font-bold ${headerText}`}>{title}</h3>
        <span className={`text-lg font-semibold ${headerText}`}>
          Total: {formatCurrency(totalValue)}
        </span>
      </div>

      {/* Items List */}
      <div className="divide-y divide-border">
        {items.length === 0 ? (
          <div className="px-6 py-8 text-center text-text-muted">
            No {isRisk ? 'risks' : 'mitigations'} recorded
          </div>
        ) : (
          items.map((item) => (
            <div key={item.id} className="px-6 py-4">
              <div className="flex justify-between items-start mb-2">
                <span className="text-text-primary font-semibold">{item.customerName}</span>
                <span className={`font-bold ${valueColor}`}>
                  {formatCurrency(item.value)}
                </span>
              </div>
              <p className="text-text-muted text-sm">{item.comment}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RiskMitigationBox;
